import type { ForecastInProgress } from "@sooth-labs/idl";
import type { Headline } from "./progressView.js";
import type { ListSummary, NotifyPrefs } from "../../types/persistence.js";
// eventIdentity imports only the ForecastEvent TYPE back from here, so this pair
// carries no runtime cycle.
import { stampOccurrenceKeys } from "./eventIdentity.js";

/** Pure module: no React, DOM or network dependencies. */

export const STATUS = Object.freeze({
  PREPROCESSING: "preprocessing",
  RUNNING: "running",
  COMPLETED: "completed",
  ERRORED: "errored",
  CANCELLED: "cancelled",
});

export const ROLE = Object.freeze({
  USER: "user",
  ASSISTANT: "assistant",
  SYSTEM: "system",
  MILESTONE: "milestone",
});

export type Status = (typeof STATUS)[keyof typeof STATUS];
export type Role = (typeof ROLE)[keyof typeof ROLE];

// ── Wire-payload placeholder types ──
// Server-sourced blobs stored on a run, typed loosely (optional named fields plus an index
// signature) until generated OpenAPI types replace them, so passthrough code needs no casts.

/** Server-sourced; `isPast` is stamped client-side, not by the server. */
export interface ForecastEvent {
  date: string;
  date_end?: string | null;
  direction?: string | null;
  p_event?: number;
  p_conditional?: number | null;
  a?: number | null; // P(question resolves YES | the trigger occurs); future events only
  b?: number | null; // P(question resolves YES | the trigger does not occur); future events only
  /**
   * Multiway only: the outcome distribution given the trigger occurs. Two shapes circulate:
   * the report/persisted wire keys it by CANONICAL outcome label (`{China: 0.58, …}`), while the
   * live progress stream (`liveTriggers.ts`, `_live` events) carries the bare mass vector in
   * label order. Only the label-keyed form is derivable — a live trigger is never branched.
   */
  a_pmf?: Record<string, number> | number[] | null;
  /** Multiway only: the outcome distribution given the trigger does not occur, in the same
   *  two shapes as `a_pmf`. */
  b_pmf?: Record<string, number> | number[] | null;
  source_url?: string | string[];
  isPast?: boolean;
  /**
   * Whether a PAST trigger actually happened. `false` marks a graded past trigger that did not
   * occur; `undefined` means "not graded" and is assumed to have occurred. `p_event` is pinned
   * to 1.0 for past events, so it cannot express this.
   */
  occurred?: boolean;
  /**
   * Stable cross-vintage occurrence identity, see `eventIdentity.ts`.
   *
   * Unlike `id`, which the run names per vintage (`e1`, `h3`, restarting every run) and which
   * is the join key back to run artifacts, this stays constant across reforecasts.
   *
   * Optional; `eventKey()` falls back to `id` when it is absent.
   */
  occurrenceKey?: string;
  [key: string]: unknown;
}

export interface Contract {
  question?: string;
  title?: string;
  probability?: number;
  [key: string]: unknown;
}

export interface ServerContext {
  explanation?: unknown;
  report?: unknown;
  metadata?: { orchestrator?: string | null; [key: string]: unknown };
  [key: string]: unknown;
}

/** Observability blob stamped on a completed run. */
export interface RunObs {
  latencyMs?: number;
  [key: string]: unknown;
}

/** Live/hydrated run progress. Unified shape across the live SSE + reload paths. */
export interface RunProgress {
  stage: string | null;
  // Backend fine-step name (e.g. "agent_turn"). Permissive: hydrate passes the
  // persisted `last_step` through as-is, and legacy rows can carry a numeric step —
  // progressStepIndex keys off it defensively (an unrecognized value → stage fallback).
  step: string | number | null;
  // Canonical progress-step index (see lib/forecast/progressSteps). -1 = unknown.
  // The reducer keeps this monotonic so the "Step N/X" line never regresses.
  stepIdx: number;
  pct: number | null;
  message: string | null;
}

/** Compact render state for the newest live tool call. */
export interface ProgressActivity {
  callId: string;
  tool: string | null;
  title: string | null;
  detail: Record<string, unknown>;
  seq: number;
  ok?: boolean;
}

/**
 * What the seed forecast turned out to be, captured as its frames arrive.
 *
 * Held here rather than read back off `progressDoc` because both fields it
 * mirrors are OVERWRITTEN later in the same run — `doc.forecast` becomes the
 * tail revision and `doc.report` the tail report — so by the time anything
 * renders there is no way to recover what the seed itself said.
 *
 * `headline` and `summary` fill in independently: the seed's number lands on
 * its `forecast_updated` and its prose on the `report_updated` after it, and a
 * failed seed (the seedless fallback) sends neither. Consumers must render a
 * partial outcome rather than wait for both.
 */
export interface SeedOutcome {
  /** The seed's own `tool_call_finished.ok`; false is the seedless fallback. */
  ok: boolean;
  headline: Headline | null;
  summary: string | null;
}

/**
 * Narration lives in its own append-only list rather than inside `progressDoc.activity`,
 * which the reducer caps at 100 entries; sharing it would silently drop a long run's
 * earliest messages.
 *
 * `ordinal` is the narration stream's own counter, unrelated to an IDL `seq`; it only
 * dedupes a replayed beat.
 */
export interface NarrationBeat {
  ordinal: number;
  text: string;
  phase: string | null;
}

/** A research thread a deep run opened (a `spawn_researcher` call): its brief, and whether it has reported back. */
export interface ResearchThread {
  callId: string;
  mission: string;
  depth: number | null;
  done: boolean;
}

/**
 * An evidence card as a running forecast announces it: its number, title, source and angle.
 * Its full text, summary and reliability arrive only with the finished report.
 */
export interface EvidenceStub {
  /** The card's running number (`card_count`), as the finished report numbers it. */
  n: number;
  title: string;
  /** The source's host ("www.bls.gov"), "article" for an archive hit, or "". */
  source: string;
  url: string;
  angle: string;
}

/**
 * What a research run (a Mirabilis lane) has gathered so far, folded from its typed stream
 * (lib/forecast/deepProgress.ts). Kept beside `progressDoc` rather than read off its
 * `activity`, which the reducer caps at 100 entries: a long run would drop its first threads.
 */
export interface ResearchProgress {
  /** Sources read so far (the newest evidence card's running count). */
  sources: number;
  /** When the newest evidence card arrived (its `occurred_at`), or null before the first. */
  lastSourceAt: string | null;
  threads: readonly ResearchThread[];
  cards: readonly EvidenceStub[];
}

export const EMPTY_RESEARCH: ResearchProgress = Object.freeze({
  sources: 0,
  lastSourceAt: null,
  threads: Object.freeze([]) as readonly ResearchThread[],
  cards: Object.freeze([]) as readonly EvidenceStub[],
});

const TERMINAL = Object.freeze([STATUS.COMPLETED, STATUS.ERRORED, STATUS.CANCELLED]);

export function isTerminal(status: string): boolean {
  return (TERMINAL as readonly string[]).includes(status);
}

/**
 * The detail blobs the boot backfill carries and a lean row omits.
 *
 * ONE definition: `loadForecasts` builds its patch from it, `DETAIL_HYDRATED` decides what a
 * local completion owns from it, and the two drifting apart would silently reintroduce the
 * overwrite race this list exists to prevent.
 *
 * Read it against the lean SELECT in `server/handlers/listForecasts.js`: anything the server
 * drops from the lean projection must appear here, or the backfill will never fill it.
 */
export const DETAIL_FIELDS = [
  "contract",
  "events",
  "serverExplanation",
  "serverContext",
  "report",
  "history",
] as const;

export type { NotifyPrefs };

/**
 * A forecast run, keyed by a client-minted `runId`. Lifecycle fields are precisely typed;
 * server-sourced payload fields use the permissive placeholder types above.
 */
export interface Run {
  runId: string;
  conversationId: string;
  parentRunId: string | null;
  status: Status;
  progress: RunProgress | null;
  progressDoc: ForecastInProgress | null;
  progressForecastRevisions: number;
  progressReportRevisions: number;
  progressSeed: boolean;
  progressSeedArmed: boolean;
  progressSeedReportArmed: boolean;
  progressSeedReportSeen: boolean;
  progressSeedStarted: boolean;
  progressSeedOutcome: SeedOutcome | null;
  progressTailReport: boolean;
  progressActivity: ProgressActivity | null;
  narration: readonly NarrationBeat[];
  progressResearch: ResearchProgress;
  traceId: string | null;
  forecastId: string | null;
  backend: string | null;
  error: string | null;
  /** The server's error code (e.g. `internal_error`, `http_429`) when the failure carried one. */
  errorCode: string | null;
  question: string;
  contract: Contract | null;
  events: ForecastEvent[] | null;
  serverContext: ServerContext | null;
  createdAt: string;
  forkDate: string | null;
  modification: string | null;
  /** What this branch ASSERTED — "occurs" | "not_occurs" | "reschedule" |
   *  "custom" — persisted in the row's `fork` jsonb at branch-create. Null on
   *  roots, and on branch rows created before it was persisted (those fall back
   *  to derivation; see `branchPremise.ts::premiseSenseOf`). */
  forkSense: string | null;
  /** Premise grading state for this branch's fork node ({state, verdict, source,
   *  gradedAt, lastAttemptAt, resolvableKey}), from the same `fork` jsonb. Null
   *  until the premise has first been looked at. Distinct from `resolution`,
   *  which is the QUESTION's verdict — see `branchPremise.ts`. */
  premise: unknown;
  /** When CLEAR BRANCH archived this branch (ISO), from the row's `fork.clearedAt`; null
   *  while it is live. The live lane hides it; a past mark from before it still draws it —
   *  see `selectVisibleBranchRuns`. */
  clearedAt: string | null;
  schedule: unknown;
  series: Record<string, unknown>;
  resolution: unknown;
  // Data-plane policy status (deleted/resolved/active), set from the wire by hydrate and
  // projected to the legacy forecast.
  contractStatus: unknown;
  /** The viewer's per-forecast alert prefs off the wire (`forecasts.notify` for the owner,
   *  `forecast_notify` for a shared row). Server-owned and read by `getTrackingPrefs`, where
   *  it wins over the localStorage cache; `{}` until the user has set any. */
  notify: NotifyPrefs;
  // Machine-mint lineage ({parentForecastId, eventId, parentQkey}) for trigger-minted
  // questions; null for human-created forecasts. Set from the wire by hydrate.
  triggerOrigin: unknown;
  /** The preprocessed question off the wire (`contract.question`), carried even on a
   *  lean row that has no contract. */
  contractQuestion: string | null;
  /** The lean list's card keys of `contract` / `history` (lib/forecast/listSource.js); null
   *  on a full row. */
  listSummary: ListSummary | null;
  alert: boolean;
  lastReforecastedAt: string | null;
  /** When the current run began (ISO): the wire's `startedAt` on hydrate, the click on a
   *  manual reforecast's RUN_STARTED. Absent on a fresh run, which starts at createdAt. */
  startedAt?: string | null;
  // True only between a manual reforecast's RUN_STARTED and its new trace landing: the run
  // is RUNNING but still carries the PREVIOUS run's traceId, so it must not be offered as a
  // cancel target (see selectCancellableRun).
  awaitingTrace?: boolean;
  debug: unknown;
  obs: RunObs | null;
  collapsed: boolean;
  partial: boolean;
  forkEvent: ForecastEvent | null;
  /** Non-null only for complete-on-creation branches derived from a machine trigger. */
  forkDerived: unknown;
  /** A derived branch's own trigger generation (`fork.branchTriggers.status`), if claimed. */
  derivedTriggersStatus?: string | null;
  seriesImpacts: Record<string, unknown>;
  /** Persisted result headline. Kept distinct from the contract fallback because
   *  legacy rows can carry a newer server_pred than contract.probability. */
  serverPrediction: number | null;
  serverExplanation: unknown;
  report: unknown;
  history: unknown[];
  move: unknown;
  isTourExample: boolean;
  contractKey: string | null;
  contractKeyStatus: string | null;
  triggerModel: string | null;
  reportPointers: unknown[];
  /**
   * True while this run was hydrated from the LEAN list and its detail blobs
   * (contract, events, serverExplanation, the full serverContext) have not
   * arrived yet. Set only by lean boot hydration; cleared by the DETAIL_HYDRATED
   * backfill. The save effect refuses to persist a run in this state — POSTing
   * one would upsert nulls over the very columns it hasn't loaded.
   */
  detailPending: boolean;
  /** Detail arrived from a LOCAL completion rather than the boot backfill. */
  detailLocal?: boolean;
  /** Which detail blobs that completion wrote; the backfill fills only the rest. */
  detailLocalFields?: string[];
  /**
   * In memory only: the vintages scheduled promotions replaced when no history entry could
   * be filed for them (a legacy trail with no datable moment), oldest first. What a
   * post-resolution rollback restores when the history holds nothing (postResolutionRuns).
   */
  superseded?: unknown[];
  /** A post-resolution rollback restored this run's display; its saves must say so, since an
   *  empty detail field otherwise means "absent" to the server. Client-only, kept across
   *  hydrate overlays (none carries it). */
  vintageRestored?: boolean;
  /**
   * Provenance. `true` for every owner-leg row; `rowToRun` defaults it to `true` when the
   * wire row doesn't carry it. Only `false` for a row hydrated from
   * `GET /api/forecasts/shared`. No other code should flip this: provenance comes from
   * WHICH ENDPOINT returned the row, not from inspecting a row's other fields.
   */
  isOwn: boolean;
  /**
   * The forecast's owning user — present only on a shared-leg row (`isOwn: false`);
   * `null` on every owner-leg row. Never used as a provenance signal.
   */
  owner: RunOwner | null;
  /**
   * Whether this viewer may write the SHARED reforecast cadence — the cadence is
   * a property of the question, so an in-org collaborator may change it and an
   * out-of-org one may not (they get their own copy instead).
   *
   * Server-reported, never derived client-side: it comes from the same
   * `canSetSharedCadence` that `PUT /api/forecasts/:id/cadence` enforces, so the
   * slider a recipient is shown cannot drift from the write the route accepts.
   * It is a rendering hint, not the check — the route re-answers it every time.
   */
  canEdit: boolean;
  /**
   * Whether this forecast has at least one share row — a bare boolean, never
   * a count and never the subjects (see `listForecasts.js`'s IS_SHARED_COL).
   * It lets the SHARE control show SHARING without opening the dialog.
   *
   * Defaults FALSE — the opposite of `isOwn` above, and deliberately so.
   * Absence must resolve to the quiet answer in each case: a run with no
   * provenance is the owner's, a run with no share information is not shared.
   * A lean query, an older cached response, or the shared lane (which strips
   * the field) must never make the UI claim a share that may not exist.
   */
  isShared: boolean;
  /**
   * Somebody OTHER than the viewer has this on their watchlist. Owner leg only.
   */
  othersWatching: boolean;
  /**
   * When the VIEWER got this forecast (ISO): the grant time on the shared leg,
   * absent on the owner leg (see `rowToWire`). The Forecast Manager sorts on it,
   * so a newly shared forecast lands at the top of the list rather than at its
   * original creation date.
   */
  receivedAt: string | null;
}

/** The owner attribution produced by `toSharedWire`. */
export interface RunOwner {
  name: string | null;
  email: string | null;
}

export interface MakeRunArgs {
  runId: string;
  conversationId: string;
  parentRunId?: string | null;
  question?: string;
  backend?: string | null;
  status?: Status;
  forkDate?: string | null;
  modification?: string | null;
  forkSense?: string | null;
  premise?: unknown;
  clearedAt?: string | null;
  createdAt?: string;
  schedule?: unknown;
  series?: Record<string, unknown>;
  resolution?: unknown;
  contractStatus?: unknown;
  notify?: NotifyPrefs;
  triggerOrigin?: unknown;
  contractQuestion?: string | null;
  listSummary?: ListSummary | null;
  alert?: boolean;
  lastReforecastedAt?: string | null;
  debug?: unknown;
  obs?: RunObs | null;
  collapsed?: boolean;
  partial?: boolean;
  forkEvent?: ForecastEvent | null;
  forkDerived?: unknown;
  seriesImpacts?: Record<string, unknown>;
  serverPrediction?: number | null;
  serverExplanation?: unknown;
  report?: unknown;
  history?: unknown[];
  move?: unknown;
  isTourExample?: boolean;
  contractKey?: string | null;
  contractKeyStatus?: string | null;
  triggerModel?: string | null;
  reportPointers?: unknown[];
  detailPending?: boolean;
  isOwn?: boolean;
  owner?: RunOwner | null;
  canEdit?: boolean;
  isShared?: boolean;
  othersWatching?: boolean;
  receivedAt?: string | null;
}

/**
 * Roots default to 'preprocessing'; callers pass status:'running' for branches and
 * compare legs (they skip preprocess). createdAt is injectable for deterministic tests.
 */
export function makeRun({
  runId,
  conversationId,
  parentRunId = null,
  question = "",
  backend = null,
  status = STATUS.PREPROCESSING,
  forkDate = null,
  modification = null,
  forkSense = null,
  premise = null,
  clearedAt = null,
  createdAt,
  schedule = null,
  series = {},
  resolution = null,
  contractStatus = null,
  notify = {},
  triggerOrigin = null,
  contractQuestion = null,
  listSummary = null,
  alert = false,
  lastReforecastedAt = null,
  debug = null,
  obs = null,
  collapsed = false,
  partial = false,
  forkEvent = null,
  forkDerived = null,
  seriesImpacts = {},
  serverPrediction = null,
  serverExplanation = null,
  report = null,
  history = [],
  move = null,
  isTourExample = false,
  contractKey = null,
  contractKeyStatus = null,
  triggerModel = null,
  reportPointers = [],
  detailPending = false,
  // The only place a bare `true` default belongs; `rowToRun` overrides it from the wire
  // row when hydrating a shared-leg row.
  isOwn = true,
  owner = null,
  // Same direction as isOwn, and for the same reason: a run with no provenance
  // is the owner's, and an owner may always set their own cadence. Only the
  // shared leg ever sends `false`.
  canEdit = true,
  isShared = false,
  othersWatching = false,
  receivedAt = null,
}: MakeRunArgs): Run {
  if (!runId) throw new Error("makeRun: runId is required");
  if (!conversationId) throw new Error("makeRun: conversationId is required");
  return {
    runId,
    conversationId,
    parentRunId,
    status,
    progress: null,
    progressDoc: null,
    progressForecastRevisions: 0,
    progressReportRevisions: 0,
    progressSeed: false,
    progressSeedArmed: false,
    progressSeedReportArmed: false,
    progressSeedReportSeen: false,
    progressSeedStarted: false,
    progressSeedOutcome: null,
    progressTailReport: false,
    progressActivity: null,
    narration: [],
    progressResearch: EMPTY_RESEARCH,
    traceId: null,
    forecastId: null,
    backend,
    error: null,
    errorCode: null,
    question,
    contract: null,
    events: null,
    serverContext: null,
    createdAt: createdAt ?? new Date().toISOString(),
    forkDate,
    modification,
    forkSense,
    premise,
    clearedAt,
    schedule,
    series,
    resolution,
    contractStatus,
    notify,
    triggerOrigin,
    contractQuestion,
    listSummary,
    alert,
    lastReforecastedAt,
    debug,
    obs,
    collapsed,
    partial, // legacy `_partial` (incomplete-result warning)
    forkEvent,
    forkDerived,
    seriesImpacts, // branch: fork-trigger event + TSD impact estimates
    serverPrediction,
    serverExplanation,
    report, // top-level result fields (preserved across reforecast independently of serverContext)
    history, // trail of reforecast snapshots
    move, // server-sourced watchlist move signal (fallback computed client-side)
    // True ONLY for the onboarding tour's fixture forecast; gates guide-narration persistence;
    // never emitted to the legacy/wire shape.
    isTourExample,
    contractKey, // data-plane BT row key (e.g. "User#<hash>"); null until hydrated/set
    contractKeyStatus, // contract lifecycle status at key-set time; null until set
    // In-memory effective trigger-LLM id for this run (bare OpenRouter id or null). Session-only:
    // not persisted or hydrated, and the source of truth for in-session branch inheritance.
    triggerModel,
    // Append-only forecast/resolution report-pointer history; the read path fetches the
    // latest via /api/forecast|resolve/report.
    reportPointers,
    detailPending,
    isOwn,
    canEdit,
    isShared,
    othersWatching,
    receivedAt,
    owner,
  };
}

export interface Entry {
  role: Role;
  kind: string | null;
  runId: string | null;
  content: string;
  ts: string;
  metadata: Record<string, unknown>;
}

export interface MakeEntryArgs {
  role: Role;
  kind?: string | null;
  runId?: string | null;
  content?: string;
  ts?: string;
  metadata?: Record<string, unknown> | null;
}

/**
 * `metadata` is an opaque bag for keys not promoted to fields, preserved verbatim across
 * the messages round-trip.
 */
export function makeEntry({
  role,
  kind = null,
  runId = null,
  content = "",
  ts,
  metadata = {},
}: MakeEntryArgs): Entry {
  if (!role) throw new Error("makeEntry: role is required");
  // Shallow-copy the bag (and normalize null → {}) so a caller mutating the
  // input object later can't reach into stored state, and downstream code can
  // always treat metadata as an object.
  return {
    role,
    kind,
    runId,
    content,
    ts: ts ?? new Date().toISOString(),
    metadata: { ...(metadata ?? {}) },
  };
}

/**
 * The instant an event stops being "future", in the VIEWER's timezone, as ms.
 *
 * A bare `YYYY-MM-DD` denotes a whole LOCAL day, so it stays future until that
 * day ENDS (the next local midnight) — the same date-only convention
 * `resolutionMomentLocal` uses in formatDates.js. Parsing it with
 * `new Date("YYYY-MM-DD")` instead yields UTC *midnight*, i.e. the day's START:
 * west of UTC that marks a day past hours early, and for a SAME-DAY event it is
 * wrong for the whole day — which silently re-stamped every same-day trigger as
 * past, leaving a same-day forecast with no future triggers to draw.
 *
 * A value carrying a time is honoured exactly. NaN for anything unparseable, so
 * callers keep their existing drop / fall-back-to-`date` behaviour.
 */
export function eventEndMs(value: string): number {
  // Length 10 is the date-only form. A same-length non-date ("not-a-date")
  // still fails to parse below, so this needs no separate shape check.
  if (typeof value === "string" && value.length === 10) {
    const d = new Date(`${value}T00:00:00`); // LOCAL midnight, not UTC
    if (isNaN(d.getTime())) return NaN;
    d.setDate(d.getDate() + 1);
    return d.getTime();
  }
  return new Date(value).getTime();
}

/**
 * Drops events with a missing or unparseable `date`, then stamps `isPast` on the rest, mutating
 * them in place. `isPast` uses `date_end`, falling back to `date` when `date_end` is unparseable
 * (an Invalid Date compares false against everything).
 */
export function filterAndStampEvents(
  events: ForecastEvent[] | null | undefined,
  now: Date = new Date(),
): ForecastEvent[] {
  const nowMs = now.getTime();
  const kept = (events || []).filter((e) => {
    if (!e.date) return false;
    const dMs = eventEndMs(e.date);
    if (isNaN(dMs)) return false;
    const endMs = e.date_end ? eventEndMs(e.date_end) : dMs;
    e.isPast = (isNaN(endMs) ? dMs : endMs) <= nowMs;
    return true;
  });
  // This is the single funnel every ingest path runs through; hydration bypasses it and
  // stamps separately. Stamping is idempotent: an event that already carries a key keeps it.
  return stampOccurrenceKeys(kept);
}

/**
 * Clears a stored `isPast: true` on any event whose effective end (the `filterAndStampEvents`
 * rule) is still after `now`, so a saved row can never draw an occurred ✓ right of the today
 * line. One-directional: a stale `isPast: false` is left alone. Returns the input array when
 * nothing changes (memo stability); changed events are cloned, never mutated.
 */
export function clearFuturePastFlags<T extends ForecastEvent[] | null | undefined>(
  events: T,
  now: Date,
): T {
  if (!Array.isArray(events)) return events;
  const nowMs = now.getTime();
  let changed = false;
  const out = events.map((e) => {
    if (e.isPast !== true || !e.date) return e;
    const dMs = eventEndMs(e.date);
    if (isNaN(dMs)) return e;
    const endMs = e.date_end ? eventEndMs(e.date_end) : dMs;
    if ((isNaN(endMs) ? dMs : endMs) <= nowMs) return e;
    changed = true;
    return { ...e, isPast: false };
  });
  return (changed ? out : events) as T;
}
